import {
  CANVAS_AI_MAX_MODEL_OPTIONS,
  OPENROUTER_ENDPOINTS,
} from "./canvasAi.constants";

export class OpenRouterApiError extends Error {
  constructor(message, status = 0) {
    super(message);
    this.name = "OpenRouterApiError";
    this.status = status;
  }
}

const readErrorMessage = async (response) => {
  try {
    const payload = await response.json();
    return payload?.error?.message ?? "OpenRouter request failed";
  } catch {
    return "OpenRouter request failed";
  }
};

const requestOpenRouterJson = async ({ apiKey, signal, url }) => {
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${apiKey}` },
    signal,
  });
  if (!response.ok) {
    throw new OpenRouterApiError(await readErrorMessage(response), response.status);
  }

  return response.json();
};

const hasToolAndTextSupport = (model) => {
  const outputs = model?.architecture?.output_modalities ?? [];
  const parameters = model?.supported_parameters ?? [];
  return outputs.includes("text") && parameters.includes("tools");
};

const toPricePerMillion = (price) => {
  const numericPrice = Number(price);
  return Number.isFinite(numericPrice) ? numericPrice * 1_000_000 : null;
};

const normalizeModel = (model) => ({
  completionPrice: toPricePerMillion(model.pricing?.completion),
  contextLength: Number(model.context_length) || 0,
  id: model.id,
  name: model.name || model.id,
  promptPrice: toPricePerMillion(model.pricing?.prompt),
  provider: String(model.id).split("/")[0] || "other",
});

/** @returns {Promise<object>} */
export const validateOpenRouterKey = async ({ apiKey, signal }) =>
  requestOpenRouterJson({ apiKey, signal, url: OPENROUTER_ENDPOINTS.key });

/** @returns {Promise<Array<object>>} */
export const fetchOpenRouterModels = async ({ apiKey, signal }) => {
  const payload = await requestOpenRouterJson({
    apiKey,
    signal,
    url: OPENROUTER_ENDPOINTS.models,
  });

  return (Array.isArray(payload?.data) ? payload.data : [])
    .filter(hasToolAndTextSupport)
    .map(normalizeModel)
    .filter((model) => Boolean(model.id))
    .sort((first, second) => first.name.localeCompare(second.name));
};

export const searchOpenRouterModels = ({
  limit = CANVAS_AI_MAX_MODEL_OPTIONS,
  models,
  query,
}) => {
  const normalizedQuery = String(query).trim().toLowerCase();
  const matchingModels = normalizedQuery
    ? models.filter((model) =>
        `${model.name} ${model.id} ${model.provider}`
          .toLowerCase()
          .includes(normalizedQuery)
      )
    : models;

  return matchingModels.slice(0, limit);
};
